import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes, useLocation, Link, Navigate } from 'react-router-dom';
import { analytics } from '@shared/posthog/analytics';

import { Home, LogOut, User, X } from 'lucide-react';

import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { usePilotFirstPlanWaived } from '@/shared/hooks/use-active-pilot-program';
import {
  AdvisorOnboardingHeader,
  AdvisorOnboardingFooter,
  AdvisorOnboardingBasicInfoStep,
  AdvisorOnboardingStartTrialStep,
  AdvisorOnboardingInviteClientStep,
  AdvisorOnboardingConnectEmailStep,
  AdvisorOnboardingQuestionnaireStep,
  useConsentStep,
  AdvisorOnboardingConsentStep,
  StepCompletedOverlay,
  useSubscriptionCheck,
  useOnboardingNavigation,
  useQuestionnaire,
  ONBOARDING_STEP_CONFIGS,
  getOnboardingStepConfigs,
} from '@client/features/advisor-onboarding';
import {
  PartnerConnectionSection,
  useIsDistributionPartnerContact,
} from '@client/features/distribution-partners';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@client/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@client/components/ui/dropdown-menu';
import { Button } from '@client/components/ui/button';

import { selectSession, selectFeatureFlags } from '@client/model/selectors';
import { useAdvisorOnboarding } from '@client/hooks/useAdvisorOnboarding';
import { useAdvisorOnboardingBasicInfoForm } from '@client/hooks/useAdvisorOnboardingBasicInfoForm';
import { useAdvisorConsents } from '@client/hooks/useAdvisorConsents';
import { useSignOut } from '@client/components/hooks/use-sign-out';
import { useRoleSwitch } from '@client/hooks/useRoleSwitch';
import { toast } from 'sonner';
import { isPilotCapabilityRestricted, SessionType } from '@shared';

interface AdvanceAdvisorOnboardingOptions {
  advisorId: string;
  currentStep: AdvisorOnboardingUIStep;
  isLastStep: boolean;
  hasAgreedToTermsOfService: boolean;
  hasAgreedToSmsConsent: boolean;
  saveRequiredConsents: () => Promise<unknown>;
  goNext: () => void;
  refreshSessionAndGoToDashboard: () => void;
}

export async function advanceAdvisorOnboarding({
  advisorId,
  currentStep,
  isLastStep,
  hasAgreedToTermsOfService,
  hasAgreedToSmsConsent,
  saveRequiredConsents,
  goNext,
  refreshSessionAndGoToDashboard,
}: AdvanceAdvisorOnboardingOptions) {
  if (currentStep === 'consent') {
    const needsTos = !hasAgreedToTermsOfService;
    const needsSms = !hasAgreedToSmsConsent;

    if (needsTos || needsSms) {
      try {
        await saveRequiredConsents();
      } catch (error) {
        console.error('Failed to save consents:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'An unexpected error occurred';
        toast.error('Failed to save consents', {
          description: `${errorMessage}. Please try again.`,
        });
        return;
      }
    }
  }

  if (isLastStep) {
    analytics.auth.advisorOnboardingCompleted({ advisorId });
    refreshSessionAndGoToDashboard();
    return;
  }

  analytics.auth.advisorOnboardingStepCompleted({ advisorId, currentStep });
  goNext();
}

export function AdvisorOnboarding({ advisorId }: AdvisorIdProps) {
  const location = useLocation();
  const handleSignOut = useSignOut();
  const { switchRole } = useRoleSwitch();
  const session = useSelector(selectSession);
  const { advisor } = session;
  const featureFlags = useSelector(selectFeatureFlags);
  const isWizardSession = session.sessionType === SessionType.wizard;
  // Any pilot whose first-plan options cannot produce a client, not just Edward Jones.
  const isFirstPlanWaived = usePilotFirstPlanWaived();
  const isEmailConnectionRestricted = isPilotCapabilityRestricted(featureFlags, 'email-connection');
  const isDistributionPartnersRestricted = isPilotCapabilityRestricted(
    featureFlags,
    'distribution-partners',
  );
  const isGenworthMode = !!featureFlags.genworthMode;
  const { flags } = useFeatureFlags();
  const { hideDistributionPartners } = useIsDistributionPartnerContact();

  const onboardingSteps = useMemo(
    () =>
      getOnboardingStepConfigs(featureFlags, advisor, flags, hideDistributionPartners),
    [featureFlags, advisor, flags, hideDistributionPartners],
  );

  const advisorName = advisor
    ? `${advisor.advisorFirstName ?? ''} ${advisor.advisorLastName ?? ''}`.trim()
    : null;
  const advisorEmail = advisor?.advisorEmail ?? null;

  const {
    markStepAsCompleted,
    isStepCompleted,
    completedSteps,
    isLoading: isOnboardingLoading,
  } = useAdvisorOnboarding(advisorId);

  const currentStep: AdvisorOnboardingUIStep = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const onboardingIdx = segments.indexOf('onboarding');
    const segmentAfter = onboardingIdx >= 0 ? segments[onboardingIdx + 1] : undefined;
    const step = onboardingSteps.find(s => s.path === segmentAfter);

    return step?.id ?? 'basic-info';
  }, [location.pathname, onboardingSteps]);

  const navigation = useOnboardingNavigation({
    advisorId,
    currentStep,
    markStepAsCompleted,
  });

  const questionnaire = useQuestionnaire({ advisorId });

  const basicInfoForm = useAdvisorOnboardingBasicInfoForm({
    onSaveSuccess: navigation.goNext,
    currentOrgType: questionnaire.orgType,
  });
  const consentStep = useConsentStep();

  const {
    hasAgreedToTermsOfService,
    hasAgreedToSmsConsent,
    saveRequiredConsents,
    isSaving: isConsentSaving,
    isLoading: isConsentsLoading,
  } = useAdvisorConsents({ advisorId });

  const isStepperLoading =
    isOnboardingLoading || isConsentsLoading || !basicInfoForm.isFormInitialized;

  const { hasActiveSubscription, hasPendingInvitation } = useSubscriptionCheck({ advisorId });

  const hideEmailAndFillIntakeForm = flags.hideInviteEmailAndFillIntakeForm ?? false;

  const isEmailConnected = !!(
    (advisor?.advisorOauthSettings?.google?.isConnected ??
      advisor?.advisorOauthSettings?.google?.userEmail) ||
    (advisor?.advisorOauthSettings?.microsoft?.isConnected ??
      advisor?.advisorOauthSettings?.microsoft?.userEmail)
  );

  const stepsForHeader = useMemo(() => {
    return onboardingSteps.map(step => {
      let isCompleted = false;

      if (step.id === 'consent') {
        isCompleted = hasAgreedToTermsOfService && hasAgreedToSmsConsent;
      } else if (step.id === 'activate-seat') {
        isCompleted = isStepCompleted(step.id);
      } else if (step.id === 'connect-email') {
        isCompleted = isEmailConnected;
      } else if (step.id === 'questionnaire') {
        isCompleted = questionnaire.isComplete || isStepCompleted(step.id);
      } else {
        isCompleted = isStepCompleted(step.id);
      }

      return {
        id: step.id,
        title: step.title,
        isCompleted,
      };
    });
  }, [
    hasAgreedToTermsOfService,
    hasAgreedToSmsConsent,
    isStepCompleted,
    isEmailConnected,
    onboardingSteps,
    questionnaire.isComplete,
  ]);

  const handleStepClick = useCallback(
    (stepId: string) => {
      navigation.goToStep(stepId as AdvisorOnboardingUIStep);
    },
    [navigation],
  );
  const stepFlags = useMemo(() => {
    const skippableSteps = ['activate-seat', 'consent', 'questionnaire', 'first-plan'];

    const isCurrentStepCompleted = isStepCompleted(currentStep);
    const isSkippableStepForWizard = skippableSteps.includes(currentStep);
    const isConnectEmailStep = currentStep === 'connect-email';
    const canSkip =
      (isWizardSession && isSkippableStepForWizard) ||
      (isFirstPlanWaived &&
        (currentStep === 'first-plan' || currentStep === 'distribution-partner')) ||
      isConnectEmailStep;

    return {
      isNextDisabled:
        (currentStep === 'basic-info' && !basicInfoForm.isProfileComplete) ||
        (currentStep === 'questionnaire' && !questionnaire.isComplete) ||
        (currentStep === 'consent' &&
          !(
            (consentStep.hasConsentedToTos || hasAgreedToTermsOfService) &&
            (consentStep.hasConsentedToSms || hasAgreedToSmsConsent)
          )) ||
        (currentStep === 'activate-seat' && !hasActiveSubscription) ||
        // Genworth advisors can finish from the last step (connect-email) without connecting.
        (currentStep === 'connect-email' && !isEmailConnected && !isGenworthMode) ||
        (currentStep === 'first-plan' &&
          !hideEmailAndFillIntakeForm &&
          !isStepCompleted('invite-client')),
      hideSkip: !canSkip,
      hideSkipToDashboard: !isWizardSession && !isFirstPlanWaived,
      isCurrentStepCompleted,
    };
  }, [
    currentStep,
    basicInfoForm.isProfileComplete,
    consentStep.hasConsentedToTos,
    consentStep.hasConsentedToSms,
    hasAgreedToTermsOfService,
    hasAgreedToSmsConsent,
    hasActiveSubscription,
    isEmailConnected,
    isStepCompleted,
    isWizardSession,
    isFirstPlanWaived,
    isGenworthMode,
    hideEmailAndFillIntakeForm,
    questionnaire.isComplete,
  ]);

  const handleNext = useCallback(
    () =>
      advanceAdvisorOnboarding({
        advisorId,
        currentStep,
        isLastStep: navigation.isLastStep,
        hasAgreedToTermsOfService,
        hasAgreedToSmsConsent,
        saveRequiredConsents,
        goNext: navigation.goNext,
        refreshSessionAndGoToDashboard: navigation.refreshSessionAndGoToDashboard,
      }),
    [
      advisorId,
      currentStep,
      navigation.isLastStep,
      navigation.goNext,
      navigation.refreshSessionAndGoToDashboard,
      saveRequiredConsents,
      hasAgreedToTermsOfService,
      hasAgreedToSmsConsent,
    ],
  );

  return (
    <div className="relative flex min-h-screen flex-col bg-white">
      <div className="absolute right-4 top-4 z-30 flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-700"
              aria-label="User menu"
            >
              <User className="size-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-56"
          >
            {(advisorName || advisorEmail) && (
              <>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    {advisorName && (
                      <p className="text-sm font-medium leading-none">{advisorName}</p>
                    )}
                    {advisorEmail && (
                      <p className="text-muted-foreground text-xs leading-none">{advisorEmail}</p>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
              </>
            )}
            {session.hasClientAccount && (
              <DropdownMenuItem
                onClick={() => switchRole('client', '/client/home')}
                className="cursor-pointer"
              >
                <Home className="mr-2 h-4 w-4" />
                Home
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={handleSignOut}
              className="cursor-pointer"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {(session.hasClientAccount || !stepFlags.hideSkipToDashboard) && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                {session.hasClientAccount ? (
                  <button
                    type="button"
                    onClick={() => switchRole('client', '/client/home')}
                    className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Go to home"
                  >
                    <X className="size-6" />
                  </button>
                ) : (
                  <Link
                    to={`/advisors/${advisorId}/clients`}
                    className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Go to dashboard"
                  >
                    <X className="size-6" />
                  </Link>
                )}
              </TooltipTrigger>
              <TooltipContent side="left">
                {session.hasClientAccount ? 'Go to home' : 'Go to dashboard'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <AdvisorOnboardingHeader
        currentStepId={currentStep}
        title={navigation.currentStepConfig?.title}
        steps={stepsForHeader}
        onStepClick={handleStepClick}
        isLoading={isStepperLoading}
      />

      <div className="m-auto size-full max-w-5xl p-6">
        <Routes>
          <Route
            path="basic-info"
            element={
              <AdvisorOnboardingBasicInfoStep
                form={basicInfoForm.form}
                onSubmit={basicInfoForm.onSubmit}
                isSubmitting={basicInfoForm.isSubmitting}
                isProfileComplete={basicInfoForm.isProfileComplete}
                isValid={basicInfoForm.isValid}
                isOrgLocked={basicInfoForm.isOrgLocked}
                showOrgType={basicInfoForm.showOrgType}
              />
            }
          />
          <Route
            path="questionnaire/*"
            element={<AdvisorOnboardingQuestionnaireStep advisorId={advisorId} />}
          />
          <Route
            path="activate-seat"
            element={<AdvisorOnboardingStartTrialStep advisorId={advisorId} />}
          />
          <Route
            path="consent"
            element={
              <div className="relative">
                <AdvisorOnboardingConsentStep
                  hasConsentedToTos={consentStep.hasConsentedToTos || hasAgreedToTermsOfService}
                  hasConsentedToSms={consentStep.hasConsentedToSms || hasAgreedToSmsConsent}
                  onTosConsentChange={consentStep.setHasConsentedToTos}
                  onSmsConsentChange={consentStep.setHasConsentedToSms}
                  isSubmitting={isConsentSaving}
                  hasScrolledToBottom={consentStep.hasScrolledToBottom}
                  onScrolledToBottom={consentStep.handleScrolledToBottom}
                />
                <StepCompletedOverlay
                  isVisible={hasAgreedToTermsOfService && hasAgreedToSmsConsent}
                  title="Consents accepted"
                  onContinue={navigation.goNext}
                />
              </div>
            }
          />
          <Route
            path="connect-email"
            element={
              isEmailConnectionRestricted ? (
                <Navigate
                  to={`/advisors/${advisorId}/onboarding/basic-info`}
                  replace
                />
              ) : (
                <AdvisorOnboardingConnectEmailStep />
              )
            }
          />
          <Route
            path="first-plan"
            element={
              onboardingSteps.some(step => step.id === 'first-plan') ? (
                <AdvisorOnboardingInviteClientStep />
              ) : (
                <Navigate
                  to={`/advisors/${advisorId}/onboarding/basic-info`}
                  replace
                />
              )
            }
          />
          <Route
            path="distribution-partner"
            element={
              isDistributionPartnersRestricted ? (
                <Navigate
                  to={`/advisors/${advisorId}/onboarding/basic-info`}
                  replace
                />
              ) : (
                <PartnerConnectionSection variant="onboarding" />
              )
            }
          />
        </Routes>
      </div>

      {currentStep !== 'basic-info' && currentStep !== 'questionnaire' && (
        <AdvisorOnboardingFooter
          totalSteps={onboardingSteps.length}
          currentStep={navigation.currentStepIndex}
          onNext={handleNext}
          onSkip={navigation.skip}
          onPrev={navigation.goPrev}
          isNextDisabled={stepFlags.isNextDisabled}
          isNextLoading={
            (currentStep === 'consent' && isConsentSaving) ||
            ((currentStep === 'first-plan' || currentStep === 'distribution-partner') &&
              navigation.isPendingDashboardNavigation)
          }
          hideSkip={stepFlags.hideSkip}
          lastStepButtonLabel={'Go to Dashboard'}
          allowSkipOnLastStep={isWizardSession || isFirstPlanWaived}
        />
      )}
      {currentStep === 'questionnaire' && isWizardSession && (
        <AdvisorOnboardingFooter
          totalSteps={onboardingSteps.length}
          currentStep={navigation.currentStepIndex}
          onSkip={navigation.skip}
          hideNext
          hidePrev
          hideSkip={false}
        />
      )}
    </div>
  );
}
